import React, { useState } from 'react'
import { useSetupWizard } from '../../../contexts/SetupWizardContext'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { PlusIcon, TrashIcon, CurrencyDollarIcon, TicketIcon, CalendarIcon, SparklesIcon } from '@heroicons/react/24/outline'

// Package templates based on business type
const packageTemplates = {
  ice_rink: [
    {
      name: '5 Class Pass',
      type: 'session_based',
      credits: 5,
      price: 110,
      validityDays: 90,
      description: 'Valid for any 5 skating sessions',
      applicableServices: ['class']
    },
    {
      name: '10 Class Pass',
      type: 'session_based',
      credits: 10,
      price: 200,
      validityDays: 120,
      description: 'Valid for any 10 skating sessions - Best value!',
      applicableServices: ['class']
    },
    {
      name: 'Monthly Unlimited',
      type: 'time_based',
      duration: 30,
      price: 199,
      description: 'Unlimited skating for 30 days',
      applicableServices: ['class']
    },
    {
      name: 'Hockey League Package',
      type: 'custom',
      price: 599,
      validityDays: 180,
      description: 'Full season registration with jersey',
      includes: ['League registration', 'Team jersey', 'End of season party']
    }
  ],
  training_center: [
    {
      name: '5 Session Package',
      type: 'session_based',
      credits: 5,
      price: 400,
      validityDays: 60,
      description: 'Valid for 5 personal training sessions',
      applicableServices: ['service']
    },
    {
      name: '10 Session Package',
      type: 'session_based',
      credits: 10,
      price: 750,
      validityDays: 90,
      description: 'Valid for 10 personal training sessions',
      applicableServices: ['service']
    },
    {
      name: 'Monthly Membership',
      type: 'time_based',
      duration: 30,
      price: 149,
      description: 'Unlimited access to all group classes',
      applicableServices: ['class']
    },
    {
      name: 'Transformation Package',
      type: 'custom',
      price: 999,
      validityDays: 90,
      description: '12-week transformation program',
      includes: ['12 PT sessions', 'Nutrition plan', 'Weekly check-ins', 'Body composition analysis']
    }
  ],
  sports_complex: [
    {
      name: '10 Drop-in Pass',
      type: 'session_based',
      credits: 10,
      price: 180,
      validityDays: 180,
      description: 'Valid for 10 drop-in sessions',
      applicableServices: ['class']
    },
    {
      name: 'Season Pass',
      type: 'time_based',
      duration: 180,
      price: 499,
      description: '6-month unlimited access',
      applicableServices: ['class', 'rental']
    },
    {
      name: 'Team Registration',
      type: 'custom',
      price: 1200,
      validityDays: 90,
      description: 'Full team league registration',
      includes: ['League fees', 'Referee fees', 'Field time', 'Championship entry']
    }
  ],
  dance_martial_arts: [
    {
      name: '5 Class Card',
      type: 'session_based',
      credits: 5,
      price: 110,
      validityDays: 60,
      description: 'Valid for any 5 classes',
      applicableServices: ['class']
    },
    {
      name: '10 Class Card',
      type: 'session_based',
      credits: 10,
      price: 200,
      validityDays: 90,
      description: 'Valid for any 10 classes',
      applicableServices: ['class']
    },
    {
      name: 'Monthly Unlimited',
      type: 'time_based',
      duration: 30,
      price: 175,
      description: 'Unlimited classes for 30 days',
      applicableServices: ['class']
    },
    {
      name: 'Quarterly Membership',
      type: 'time_based',
      duration: 90,
      price: 450,
      description: '3-month unlimited access - Save 15%',
      applicableServices: ['class']
    }
  ],
  other: []
}

const packageTypes = [
  { value: 'session_based', label: 'Session-Based', icon: TicketIcon, description: 'Class passes with credits' },
  { value: 'time_based', label: 'Time-Based', icon: CalendarIcon, description: 'Monthly/quarterly access' },
  { value: 'custom', label: 'Custom Package', icon: SparklesIcon, description: 'Bundled services' }
]

export default function PackagesStep() {
  const { wizardData, updateStepData, getStepErrors } = useSetupWizard()
  const colors = useThemeColors()
  const { packages, organizationProfile } = wizardData
  const errors = getStepErrors(5)
  const [activeTab, setActiveTab] = useState<'all' | 'session_based' | 'time_based' | 'custom'>('all')

  const addPackage = (type: string = 'session_based') => {
    const newPackage = {
      id: `package-${Date.now()}`,
      name: '',
      type,
      price: 0,
      description: '',
      // Session-based fields
      credits: type === 'session_based' ? 10 : undefined,
      validityDays: 90,
      applicableServices: type === 'session_based' ? ['class'] : undefined,
      // Time-based fields
      duration: type === 'time_based' ? 30 : undefined,
      // Custom package fields
      includes: type === 'custom' ? [] : undefined,
      // Common fields
      maxPurchasesPerCustomer: null,
      requiresMembership: false,
      isActive: true
    }
    updateStepData('packages', [...packages, newPackage])
    setActiveTab(type as typeof activeTab)
  }

  const updatePackage = (id: string, field: string, value: string | number | boolean | null | string[]) => {
    const updated = packages.map(pkg =>
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    )
    updateStepData('packages', updated)
  }

  const removePackage = (id: string) => {
    const updated = packages.filter(pkg => pkg.id !== id)
    updateStepData('packages', updated)
  }

  const loadTemplates = () => {
    const templates = packageTemplates[organizationProfile.businessType] || []
    const newPackages = templates.map((template, index) => ({
      id: `package-${Date.now()}-${index}`,
      ...template,
      isActive: true
    }))
    updateStepData('packages', newPackages)
  }

  const filteredPackages = activeTab === 'all' 
    ? packages 
    : packages.filter(p => p.type === activeTab)

  const getPackageIcon = (type: string) => {
    const packageType = packageTypes.find(t => t.value === type)
    return packageType?.icon || CurrencyDollarIcon
  }

  const addIncludedItem = (packageId: string) => {
    const pkg = packages.find(p => p.id === packageId)
    if (pkg && pkg.includes) {
      updatePackage(packageId, 'includes', [...pkg.includes, ''])
    }
  }

  const updateIncludedItem = (packageId: string, index: number, value: string) => {
    const pkg = packages.find(p => p.id === packageId)
    if (pkg && pkg.includes) {
      const updated = [...pkg.includes]
      updated[index] = value
      updatePackage(packageId, 'includes', updated)
    }
  }

  const removeIncludedItem = (packageId: string, index: number) => {
    const pkg = packages.find(p => p.id === packageId)
    if (pkg && pkg.includes) {
      const updated = pkg.includes.filter((_, i) => i !== index)
      updatePackage(packageId, 'includes', updated)
    }
  }

  return (
    <div className="packages-step">
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: colors.text
      }}>
        Packages & Bundles
      </h2>
      <p style={{
        fontSize: '1rem',
        color: colors.textSecondary,
        marginBottom: '2rem'
      }}>
        Create package deals to increase revenue and customer retention.
      </p>

      {/* Errors */}
      {errors.length > 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: `${colors.error}20`,
          border: `1px solid ${colors.error}40`,
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.error,
            margin: 0
          }}>
            {errors[0]}
          </p>
        </div>
      )}

      {/* Template Suggestion */}
      {packages.length === 0 && organizationProfile.businessType !== 'other' && (
        <div style={{
          padding: '1rem',
          backgroundColor: `${colors.primary}10`,
          border: `1px solid ${colors.primary}30`,
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.text,
            margin: 0
          }}>
            💡 Start with popular packages for {organizationProfile.businessType.replace(/_/g, ' ')}?
          </p>
          <button
            onClick={loadTemplates}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: colors.primary,
              color: colors.background,
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.813rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Use Templates
          </button>
        </div>
      )}

      {/* Package Type Info */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {packageTypes.map(type => (
          <div
            key={type.value}
            style={{
              backgroundColor: colors.backgroundLight,
              padding: '1rem',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <type.icon style={{ width: '20px', height: '20px', color: colors.primary }} />
              <h4 style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: colors.text,
                margin: 0
              }}>
                {type.label}
              </h4>
            </div>
            <p style={{
              fontSize: '0.75rem',
              color: colors.textSecondary,
              margin: 0
            }}>
              {type.description}
            </p>
          </div>
        ))}
      </div>

      {/* Category Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: `1px solid ${colors.border}`,
        overflowX: 'auto'
      }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'all' ? `2px solid ${colors.primary}` : '2px solid transparent',
            color: activeTab === 'all' ? colors.primary : colors.textSecondary,
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          All ({packages.length})
        </button>
        {packageTypes.map(type => {
          const count = packages.filter(p => p.type === type.value).length
          return (
            <button
              key={type.value}
              onClick={() => setActiveTab(type.value as typeof activeTab)}
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === type.value ? `2px solid ${colors.primary}` : '2px solid transparent',
                color: activeTab === type.value ? colors.primary : colors.textSecondary,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <type.icon style={{ width: '16px', height: '16px' }} />
              {type.label} ({count})
            </button>
          )
        })}
      </div>

      {/* Packages List */}
      <div style={{
        display: 'grid',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {filteredPackages.map((pkg, index) => {
          const Icon = getPackageIcon(pkg.type)
          return (
            <div
              key={pkg.id}
              style={{
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                padding: '1.5rem'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Icon style={{ 
                    width: '20px', 
                    height: '20px', 
                    color: colors.primary 
                  }} />
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: colors.text,
                    margin: 0
                  }}>
                    {pkg.name || `New ${pkg.type.replace('_', ' ')} package`}
                  </h3>
                </div>
                <button
                  onClick={() => removePackage(pkg.id)}
                  style={{
                    padding: '0.25rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: colors.error,
                    cursor: 'pointer'
                  }}
                >
                  <TrashIcon style={{ width: '20px', height: '20px' }} />
                </button>
              </div>

              <div style={{
                display: 'grid',
                gap: '1rem'
              }}>
                {/* Package Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.813rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: colors.textSecondary
                  }}>
                    Package Name <span style={{ color: colors.error }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={pkg.name}
                    onChange={(e) => updatePackage(pkg.id, 'name', e.target.value)}
                    placeholder={
                      pkg.type === 'session_based' ? 'e.g., 10 Class Pass' :
                      pkg.type === 'time_based' ? 'e.g., Monthly Unlimited' :
                      'e.g., Birthday Party Package'
                    }
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: colors.backgroundLight,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                {/* Type-specific configuration */}
                {pkg.type === 'session_based' ? (
                  <>
                    <div style={{
                      backgroundColor: colors.backgroundLight,
                      padding: '1rem',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`
                    }}>
                      <h4 style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: colors.text,
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <TicketIcon style={{ width: '16px', height: '16px' }} />
                        Session Configuration
                      </h4>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            marginBottom: '0.25rem',
                            color: colors.textSecondary
                          }}>
                            Number of Credits
                          </label>
                          <input
                            type="number"
                            value={pkg.credits || 0}
                            onChange={(e) => updatePackage(pkg.id, 'credits', parseInt(e.target.value) || 0)}
                            placeholder="10"
                            min="1"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            marginBottom: '0.25rem',
                            color: colors.textSecondary
                          }}>
                            Price ($)
                          </label>
                          <input
                            type="number"
                            value={pkg.price || 0}
                            onChange={(e) => updatePackage(pkg.id, 'price', parseFloat(e.target.value) || 0)}
                            placeholder="200"
                            min="0"
                            step="0.01"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.875rem'
                            }}
                          />
                          {pkg.credits > 0 && (
                            <p style={{
                              fontSize: '0.688rem',
                              color: colors.textMuted,
                              marginTop: '0.125rem'
                            }}>
                              ${((pkg.price || 0) / pkg.credits).toFixed(2)} per session
                            </p>
                          )}
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            marginBottom: '0.25rem',
                            color: colors.textSecondary
                          }}>
                            Valid For (days)
                          </label>
                          <input
                            type="number"
                            value={pkg.validityDays || 90}
                            onChange={(e) => updatePackage(pkg.id, 'validityDays', parseInt(e.target.value) || 90)}
                            placeholder="90"
                            min="1"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ marginTop: '1rem' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Applicable Services
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {['class', 'service', 'rental'].map(serviceType => (
                            <label
                              key={serviceType}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                padding: '0.25rem 0.75rem',
                                backgroundColor: colors.background,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '6px',
                                fontSize: '0.813rem',
                                color: colors.text,
                                cursor: 'pointer'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={pkg.applicableServices?.includes(serviceType) || false}
                                onChange={(e) => {
                                  const current = pkg.applicableServices || []
                                  if (e.target.checked) {
                                    updatePackage(pkg.id, 'applicableServices', [...current, serviceType])
                                  } else {
                                    updatePackage(pkg.id, 'applicableServices', current.filter(s => s !== serviceType))
                                  }
                                }}
                              />
                              {serviceType.charAt(0).toUpperCase() + serviceType.slice(1)}s
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : pkg.type === 'time_based' ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Duration
                        </label>
                        <select
                          value={pkg.duration || 30}
                          onChange={(e) => updatePackage(pkg.id, 'duration', parseInt(e.target.value))}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        >
                          <option value="7">1 Week</option>
                          <option value="30">1 Month</option>
                          <option value="90">3 Months</option>
                          <option value="180">6 Months</option>
                          <option value="365">1 Year</option>
                        </select>
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Price ($) <span style={{ color: colors.error }}>*</span>
                        </label>
                        <input
                          type="number"
                          value={pkg.price}
                          onChange={(e) => updatePackage(pkg.id, 'price', parseFloat(e.target.value) || 0)}
                          placeholder="149"
                          min="0"
                          step="0.01"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Custom Package */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.813rem',
                        fontWeight: '500',
                        marginBottom: '0.25rem',
                        color: colors.textSecondary
                      }}>
                        Price ($) <span style={{ color: colors.error }}>*</span>
                      </label>
                      <input
                        type="number"
                        value={pkg.price}
                        onChange={(e) => updatePackage(pkg.id, 'price', parseFloat(e.target.value) || 0)}
                        placeholder="299"
                        min="0"
                        step="0.01"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          backgroundColor: colors.backgroundLight,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>

                    {/* What's Included */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.813rem',
                        fontWeight: '500',
                        marginBottom: '0.5rem',
                        color: colors.textSecondary
                      }}>
                        What's Included
                      </label>
                      <div style={{
                        backgroundColor: colors.backgroundLight,
                        padding: '1rem',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`
                      }}>
                        {pkg.includes?.map((item, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            gap: '0.5rem',
                            marginBottom: '0.5rem'
                          }}>
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => updateIncludedItem(pkg.id, idx, e.target.value)}
                              placeholder="e.g., 2 hours ice time"
                              style={{
                                flex: 1,
                                padding: '0.5rem',
                                backgroundColor: colors.background,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '6px',
                                color: colors.text,
                                fontSize: '0.813rem'
                              }}
                            />
                            <button
                              onClick={() => removeIncludedItem(pkg.id, idx)}
                              style={{
                                padding: '0.5rem',
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: colors.error,
                                cursor: 'pointer'
                              }}
                            >
                              <TrashIcon style={{ width: '16px', height: '16px' }} />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addIncludedItem(pkg.id)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: 'transparent',
                            border: `1px dashed ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.primary,
                            fontSize: '0.813rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.25rem',
                            marginTop: pkg.includes?.length ? '0' : '0'
                          }}
                        >
                          <PlusIcon style={{ width: '16px', height: '16px' }} />
                          Add Item
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Description */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.813rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: colors.textSecondary
                  }}>
                    Description
                  </label>
                  <textarea
                    value={pkg.description || ''}
                    onChange={(e) => updatePackage(pkg.id, 'description', e.target.value)}
                    placeholder="Brief description of this package"
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: colors.backgroundLight,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      minHeight: '60px'
                    }}
                  />
                </div>

                {/* Optional Restrictions */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'center'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.813rem',
                    color: colors.text,
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={pkg.requiresMembership || false}
                      onChange={(e) => updatePackage(pkg.id, 'requiresMembership', e.target.checked)}
                    />
                    Requires Membership
                  </label>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{
                      fontSize: '0.813rem',
                      color: colors.textSecondary
                    }}>
                      Max per customer:
                    </label>
                    <input
                      type="number"
                      value={pkg.maxPurchasesPerCustomer || ''}
                      onChange={(e) => updatePackage(pkg.id, 'maxPurchasesPerCustomer', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="∞"
                      min="1"
                      style={{
                        width: '60px',
                        padding: '0.25rem',
                        backgroundColor: colors.backgroundLight,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px',
                        color: colors.text,
                        fontSize: '0.813rem'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Add Package Buttons */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '0.75rem'
      }}>
        {packageTypes.map(type => (
          <button
            key={type.value}
            onClick={() => addPackage(type.value)}
            style={{
              padding: '1rem',
              backgroundColor: 'transparent',
              border: `2px dashed ${colors.border}`,
              borderRadius: '8px',
              color: colors.primary,
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.primary
              e.currentTarget.style.backgroundColor = `${colors.primary}10`
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.border
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <PlusIcon style={{ width: '20px', height: '20px' }} />
            Add {type.label}
          </button>
        ))}
      </div>
    </div>
  )
}